/**
 * The answers of `/sales-truth/*` with every default filled in. The generated types mark
 * each field with a server default as optional, so the page reads one shape: a missing
 * count is 0, a missing list is empty, a missing flag is false. Pure, no React; the tests
 * call it directly.
 */

import type {
  CaseGroundTruth,
  LineDiffCounts,
  LineDiffRow,
  OrderLine,
  OrderQtyChange,
  SalesTruthFunnelRow,
  SalesTruthInquiry,
  SalesTruthInquiryDetail,
  SalesTruthInquiryList,
  SalesTruthKpis,
  SalesTruthPeople,
  SalesTruthPerson,
  SalesTruthSourceCount,
  SalesTruthSummary,
  TruthLearning,
  TruthLearningCounts,
  TruthLearningList,
  TruthLearnSwitchState,
} from "@/lib/api";

/** Every field present; a nullable field keeps its null. */
export type Defined<T> = { [K in keyof T]-?: Exclude<T[K], undefined> };

export type DiffRow = Defined<LineDiffRow>;
export type DiffCounts = Defined<LineDiffCounts>;
export type LearningCounts = Defined<TruthLearningCounts>;
export type Learning = Defined<TruthLearning>;
export type InquiryRow = Defined<SalesTruthInquiry>;
export type PersonRow = Defined<SalesTruthPerson>;

export type OrderOnlyLine = Defined<OrderLine>;
export type QtyChange = Defined<OrderQtyChange>;

export type GroundTruth = Omit<
  Defined<CaseGroundTruth>,
  "diff" | "diff_counts" | "order_only" | "order_credit" | "order_qty_changed"
> & {
  diff: DiffRow[];
  diff_counts: DiffCounts;
  order_only: OrderOnlyLine[];
  order_credit: OrderOnlyLine[];
  order_qty_changed: QtyChange[];
};

export interface Summary {
  window_days: number;
  as_of: string | null;
  kpis: Defined<SalesTruthKpis>;
  funnel: Defined<SalesTruthFunnelRow>[];
  sources: Defined<SalesTruthSourceCount>[];
  differences: DiffCounts;
  open_in_i2q: number;
  learnings: LearningCounts;
  switches: Defined<TruthLearnSwitchState>;
}

export interface InquiryList {
  total: number;
  limit: number;
  offset: number;
  rows: InquiryRow[];
}

export interface People {
  rows: PersonRow[];
  hidden_areas: string[];
  hidden_linked: number;
}

export interface LearningList {
  total: number;
  limit: number;
  offset: number;
  rows: Learning[];
  counts: LearningCounts;
  /** The switch `truth_learn_apply` is ON: only then can "Approve" apply a learning. */
  apply_on: boolean;
  /** The switch `truth_learn_multi_account` is ON: new drafts use the marks (D5). */
  multi_account_on: boolean;
}

export interface InquiryDetail {
  inquiry: InquiryRow;
  link: GroundTruth;
  learnings: Learning[];
  apply_on: boolean;
  multi_account_on: boolean;
}

const n = (value: number | null | undefined): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;
const v = <T>(value: T | null | undefined): T | null => (value === undefined ? null : value);

export function toDiffCounts(raw: LineDiffCounts | null | undefined): DiffCounts {
  return {
    same_item: n(raw?.same_item),
    other_item: n(raw?.other_item),
    missed: n(raw?.missed),
    extra: n(raw?.extra),
    qty_differs: n(raw?.qty_differs),
    price_differs: n(raw?.price_differs),
    not_quoted: n(raw?.not_quoted),
  };
}

export function toLearningCounts(raw: TruthLearningCounts | null | undefined): LearningCounts {
  return {
    observed: n(raw?.observed),
    confirmed: n(raw?.confirmed),
    proposed: n(raw?.proposed),
    active: n(raw?.active),
    conflict: n(raw?.conflict),
    unaligned: n(raw?.unaligned),
    retracted: n(raw?.retracted),
    multi_account_senders: n(raw?.multi_account_senders),
  };
}

export function toDiffRow(raw: LineDiffRow): DiffRow {
  return {
    verdict: raw.verdict,
    draft_index: v(raw.draft_index),
    inquiry_text: v(raw.inquiry_text),
    i2q_item: v(raw.i2q_item),
    i2q_qty: v(raw.i2q_qty),
    i2q_price: v(raw.i2q_price),
    sales_item: v(raw.sales_item),
    sales_description: v(raw.sales_description),
    sales_qty: v(raw.sales_qty),
    sales_price: v(raw.sales_price),
    sales_line_no: v(raw.sales_line_no),
    ordered: typeof raw.ordered === "boolean" ? raw.ordered : null,
    order_state: v(raw.order_state),
    quoted_qty_total: v(raw.quoted_qty_total),
    ordered_qty_total: v(raw.ordered_qty_total),
  };
}

function toOrderLine(line: OrderLine): OrderOnlyLine {
  return { item: line.item, qty: v(line.qty), line_no: v(line.line_no) };
}

const count = (value: number | null | undefined): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

export function toGroundTruth(raw: CaseGroundTruth): GroundTruth {
  return {
    source: raw.source,
    certainty: raw.certainty,
    entity: v(raw.entity),
    quotation: v(raw.quotation),
    sales_order: v(raw.sales_order),
    quote_status: v(raw.quote_status),
    quote_created_at: v(raw.quote_created_at),
    sales_person: v(raw.sales_person),
    person_hidden: raw.person_hidden === true,
    customer_number: v(raw.customer_number),
    line_agreement: v(raw.line_agreement),
    line_count: v(raw.line_count),
    modified_after_reply: v(raw.modified_after_reply),
    answer_case_id: v(raw.answer_case_id),
    checked_at: v(raw.checked_at),
    diff: (raw.diff ?? []).map(toDiffRow),
    diff_counts: toDiffCounts(raw.diff_counts),
    order_lines_total: count(raw.order_lines_total),
    order_lines_found: count(raw.order_lines_found),
    ordered_quoted: count(raw.ordered_quoted),
    order_only: (raw.order_only ?? []).map(toOrderLine),
    order_lines_source: v(raw.order_lines_source),
    order_removed: count(raw.order_removed),
    order_cancelled: count(raw.order_cancelled),
    order_qty_changed: (raw.order_qty_changed ?? []).map((change) => ({
      item: change.item,
      quoted_qty: v(change.quoted_qty),
      ordered_qty: v(change.ordered_qty),
    })),
    order_credit: (raw.order_credit ?? []).map(toOrderLine),
    customer_po: v(raw.customer_po),
    po_mail_case_id: v(raw.po_mail_case_id),
    po_mail_at: v(raw.po_mail_at),
  };
}

export function toInquiryRow(raw: SalesTruthInquiry): InquiryRow {
  return {
    case_id: raw.case_id,
    received_at: v(raw.received_at),
    area: raw.area,
    subject: v(raw.subject),
    i2q_customer: v(raw.i2q_customer),
    i2q_customer_name: v(raw.i2q_customer_name),
    fo_customer: v(raw.fo_customer),
    customer_match: raw.customer_match ?? "no_fo",
    quotation: v(raw.quotation),
    sales_order: v(raw.sales_order),
    quote_status: v(raw.quote_status),
    quote_created_at: v(raw.quote_created_at),
    sales_person: v(raw.sales_person),
    sales_person_id: v(raw.sales_person_id),
    person_hidden: raw.person_hidden === true,
    source: raw.source,
    certainty: raw.certainty,
    line_agreement: v(raw.line_agreement),
    line_count: v(raw.line_count),
    modified_after_reply: v(raw.modified_after_reply),
    learnings: n(raw.learnings),
    conflicts: n(raw.conflicts),
    open_in_i2q: raw.open_in_i2q === true,
    customer_po: v(raw.customer_po),
    po_mail_case_id: v(raw.po_mail_case_id),
  };
}

export function toLearning(raw: TruthLearning): Learning {
  return {
    learning_id: raw.learning_id,
    case_id: raw.case_id,
    area: raw.area,
    kind: raw.kind,
    status: raw.status,
    observe_only: raw.observe_only === true,
    observe_reason: v(raw.observe_reason),
    line_index: v(raw.line_index),
    inquiry_text: v(raw.inquiry_text),
    i2q_value: v(raw.i2q_value),
    sales_value: v(raw.sales_value),
    customer_account: v(raw.customer_account),
    alignment: v(raw.alignment),
    source: raw.source,
    quotation: v(raw.quotation),
    sales_order: v(raw.sales_order),
    quote_created_at: v(raw.quote_created_at),
    sales_person: v(raw.sales_person),
    person_hidden: raw.person_hidden === true,
    alias_id: v(raw.alias_id),
    hits: n(raw.hits),
    note: v(raw.note),
    observed_at: v(raw.observed_at),
    updated_at: v(raw.updated_at),
    decided_by: v(raw.decided_by),
    sender_multi_account: raw.kind === "customer" ? raw.sender_multi_account === true : null,
    line_ordered:
      raw.kind === "item" && typeof raw.line_ordered === "boolean" ? raw.line_ordered : null,
  };
}

export function toSummary(raw: SalesTruthSummary): Summary {
  const kpis = raw.kpis;
  return {
    window_days: n(raw.window_days) || 30,
    as_of: v(raw.as_of),
    kpis: {
      inquiries: n(kpis?.inquiries),
      linked: n(kpis?.linked),
      linked_share: v(kpis?.linked_share),
      with_order: n(kpis?.with_order),
      order_share: v(kpis?.order_share),
      agreement_mean: v(kpis?.agreement_mean),
      matched_full: n(kpis?.matched_full),
      matched_share: v(kpis?.matched_share),
      ordered_lines: n(kpis?.ordered_lines),
      ordered_found: n(kpis?.ordered_found),
      ordered_found_share: v(kpis?.ordered_found_share),
      orders_with_lines: n(kpis?.orders_with_lines),
      orders_all_found: n(kpis?.orders_all_found),
      orders_with_po: n(kpis?.orders_with_po),
      orders_with_po_key: n(kpis?.orders_with_po_key),
      po_in_mail: n(kpis?.po_in_mail),
      orders_real: n(kpis?.orders_real),
      orders_changed: n(kpis?.orders_changed),
      orders_removed: n(kpis?.orders_removed),
      orders_qty_changed: n(kpis?.orders_qty_changed),
      orders_added: n(kpis?.orders_added),
      ordered_fallback_links: n(kpis?.ordered_fallback_links),
    },
    funnel: (raw.funnel ?? []).map((row) => ({
      area: row.area,
      inquiries: n(row.inquiries),
      linked: n(row.linked),
      with_order: n(row.with_order),
      ordered_lines: n(row.ordered_lines),
      ordered_found: n(row.ordered_found),
      orders_with_po: n(row.orders_with_po),
      orders_with_po_key: n(row.orders_with_po_key),
      po_in_mail: n(row.po_in_mail),
      orders_real: n(row.orders_real),
      orders_changed: n(row.orders_changed),
    })),
    sources: (raw.sources ?? []).map((row) => ({
      source: row.source,
      certainty: row.certainty,
      count: n(row.count),
    })),
    differences: toDiffCounts(raw.differences),
    open_in_i2q: n(raw.open_in_i2q),
    learnings: toLearningCounts(raw.learnings),
    switches: {
      observe: raw.switches?.observe === true,
      apply: raw.switches?.apply === true,
      auto_activate: raw.switches?.auto_activate === true,
      multi_account: raw.switches?.multi_account === true,
    },
  };
}

export function toInquiryList(raw: SalesTruthInquiryList): InquiryList {
  return {
    total: n(raw.total),
    limit: n(raw.limit) || 50,
    offset: n(raw.offset),
    rows: (raw.rows ?? []).map(toInquiryRow),
  };
}

export function toPeople(raw: SalesTruthPeople): People {
  return {
    rows: (raw.rows ?? []).map((row) => ({
      sales_person_id: row.sales_person_id,
      sales_person: v(row.sales_person),
      areas: row.areas ?? [],
      linked: n(row.linked),
      with_order: n(row.with_order),
      i2q_matched: n(row.i2q_matched),
      with_agreement: n(row.with_agreement),
      learnings: n(row.learnings),
    })),
    hidden_areas: raw.hidden_areas ?? [],
    hidden_linked: n(raw.hidden_linked),
  };
}

export function toLearningList(raw: TruthLearningList): LearningList {
  return {
    total: n(raw.total),
    limit: n(raw.limit) || 50,
    offset: n(raw.offset),
    rows: (raw.rows ?? []).map(toLearning),
    counts: toLearningCounts(raw.counts),
    apply_on: raw.apply_on === true,
    multi_account_on: raw.multi_account_on === true,
  };
}

export function toInquiryDetail(raw: SalesTruthInquiryDetail): InquiryDetail {
  return {
    inquiry: toInquiryRow(raw.inquiry),
    link: toGroundTruth(raw.link),
    learnings: (raw.learnings ?? []).map(toLearning),
    apply_on: raw.apply_on === true,
    multi_account_on: raw.multi_account_on === true,
  };
}
